import {useMutation} from "@apollo/client";
import classNames from "classnames";
import pluralize from "pluralize";
import {Dispatch, SetStateAction, useState} from "react";
import {toast} from "react-hot-toast";

import {Alert} from "@/components/alert/Alert.tsx";
import {Card} from "@/components/card/Card.tsx";
import {Chip} from "@/components/chip/Chip.tsx";
import {DeleteActionDialog} from "@/components/deleteActionDialog/DeleteActionDialog.tsx";
import {Input} from "@/components/input/Input.tsx";
import {GQL_CONTACT_GROUPS_LIST_QUERY, GQL_DELETE_CONTACT_GROUP} from "@/lib/queries/contacts.ts";
import {GraphQLResponse, ResponseStatus} from "@/lib/types/common.ts";
import {ContactGroup} from "@/lib/types/contacts.ts";

import {ContactChannelBadges} from "./ContactChannelBadges.tsx";

import styles from "./DeleteContactButtonAction.module.scss";

const CONFIRM_KEYWORD = "DELETE";

export interface DeleteContactButtonActionProps {
  contact: ContactGroup;
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
}

export const DeleteContactButtonAction = ({contact, show, setShow}: DeleteContactButtonActionProps) => {
  const [confirmText, setConfirmText] = useState("");
  const [deleteContactGroup, {reset, loading}] = useMutation<GraphQLResponse<null>>(GQL_DELETE_CONTACT_GROUP, {
    refetchQueries: [GQL_CONTACT_GROUPS_LIST_QUERY],
  });

  const close = () => {
    setShow(false);
    setConfirmText("");
    reset();
  };

  const handleSubmit = async () => {
    const response = await deleteContactGroup({
      variables: {id: contact.id, force: true},
    });
    const result = response.data?.response;
    if (result?.status === ResponseStatus.SUCCESS) {
      close();
      return;
    }
    const message =
      (result?.status === ResponseStatus.ERROR && Object.values(result.message).flat().find(Boolean)) ||
      "Failed to delete contact.";
    toast.error(message);
  };

  return (
    <DeleteActionDialog
      show={show}
      loading={loading}
      title="Delete Contact"
      confirmBtnText="Delete"
      confirmBtnVariant="red"
      disableConfirmButton={confirmText !== CONFIRM_KEYWORD}
      centered
      dialogClassName={styles.modalDialog}
      onConfirm={handleSubmit}
      onCancel={close}
    >
      <div className={styles.body}>
        <Card variant="white" className={styles.summaryBox}>
          <div className={classNames("pulseTextBodyBold", "pulse-color-gray-900", styles.name)}>{contact.name}</div>
          <ContactChannelBadges contact={contact} />
        </Card>
        <p className="pulseTextBody pulse-color-gray-800 mb-0">
          Contact is used in{" "}
          <span className="pulseTextBodyMedium">{pluralize("Check", contact.num_services, true)}</span> and{" "}
          <span className="pulseTextBodyMedium">{pluralize("Escalation", contact.num_escalations, true)}</span>.
          Deleting this contact removes it from everywhere it is currently used. This action cannot be undone.
        </p>
        <Alert variant="maintenance" size="small" className="mb-0">
          Any Checks or Escalations with no other contact assigned will not alert anyone if they go down. Review your
          Checks and assign a replacement where needed before deleting.
        </Alert>
        <div className={styles.confirm}>
          <label
            className={classNames("pulseTextBodySmallBold", "pulse-color-gray-900", styles.confirmLabel)}
            htmlFor={`delete-contact-confirm-${contact.id}`}
          >
            Type{" "}
            <Chip variant="tag" size="small">
              {CONFIRM_KEYWORD}
            </Chip>{" "}
            to confirm
          </label>
          <Input
            id={`delete-contact-confirm-${contact.id}`}
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
            autoComplete="off"
          />
        </div>
      </div>
    </DeleteActionDialog>
  );
};
